import React from "react";
import { LinePath, AreaClosed } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { GridRows } from "@visx/grid";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import { scaleUtc, scaleLinear } from "@visx/scale";
import useResizeObserver from "use-resize-observer";
import { useTheme } from "@mui/material";

export interface ChartPoint {
  timestamp: number;
  opens: number;
  openRate: number;
}

interface EngagerChartProps {
  data: ChartPoint[];
  height?: number;
  hoveredIndex: number | null;
  onHoverIndex: (index: number | null) => void;
}

const MARGIN = { top: 28, right: 24, bottom: 38, left: 52 };
const GRADIENT_ID = "engager-rate-gradient";
const LINE_COLOR = "#10b981";

function EngagerChart({
  data,
  height = 220,
  hoveredIndex,
  onHoverIndex,
}: EngagerChartProps) {
  const theme = useTheme();
  const { ref, width = 0 } = useResizeObserver<HTMLDivElement>();

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);

  const xScale = React.useMemo(() => {
    if (data.length === 0) {
      return scaleUtc({
        range: [0, innerWidth],
        domain: [new Date(), new Date()],
      });
    }
    const times = data.map((d) => d.timestamp);
    return scaleUtc({
      range: [0, innerWidth],
      domain: [Math.min(...times), Math.max(...times)],
    });
  }, [data, innerWidth]);

  const yScale = React.useMemo(() => {
    const maxRate =
      data.length > 0 ? Math.max(...data.map((d) => d.openRate)) : 100;
    return scaleLinear({
      range: [innerHeight, 0],
      domain: [0, Math.max(100, maxRate)],
      nice: true,
    });
  }, [data, innerHeight]);

  const handleMouseMove = React.useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      if (data.length === 0) return;
      const svgX = e.nativeEvent.offsetX - MARGIN.left;
      const hoverTime = xScale.invert(svgX).getTime();
      let closestIdx = 0;
      let closestDist = Infinity;
      data.forEach((d, i) => {
        const dist = Math.abs(d.timestamp - hoverTime);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      onHoverIndex(closestIdx);
    },
    [data, xScale, onHoverIndex]
  );

  const handleMouseLeave = React.useCallback(() => {
    onHoverIndex(null);
  }, [onHoverIndex]);

  const hoveredPoint = hoveredIndex != null ? data[hoveredIndex] : null;

  const tooltipW = 130;
  const tooltipH = 64;

  return (
    <div ref={ref} style={{ width: "100%", height }}>
      {width > 0 && (
        <svg width={width} height={height}>
          <defs>
            <LinearGradient
              id={GRADIENT_ID}
              from={LINE_COLOR}
              to={LINE_COLOR}
              fromOpacity={0.18}
              toOpacity={0.01}
              vertical
            />
          </defs>

          <Group left={MARGIN.left} top={MARGIN.top}>
            <GridRows
              scale={yScale}
              width={innerWidth}
              numTicks={5}
              stroke={theme.palette.grey[200]}
              pointerEvents="none"
            />
            <AxisLeft
              scale={yScale}
              hideAxisLine
              hideTicks
              numTicks={5}
              tickFormat={(val: any) => `${val}%`}
              tickLabelProps={() => ({
                fill: theme.palette.grey[600],
                fontSize: 10,
                textAnchor: "end",
                dy: "0.33em",
                dx: "-4",
              })}
            />
            <AxisBottom
              top={innerHeight}
              scale={xScale}
              hideAxisLine
              hideTicks
              tickValues={data
                .filter((_, i) => {
                  const interval =
                    data.length <= 7 ? 1 : data.length <= 14 ? 2 : 7;
                  return i % interval === 0;
                })
                .map((d) => new Date(d.timestamp))}
              tickFormat={(d: any) =>
                new Date(d).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
              }
              tickLabelProps={() => ({
                fill: theme.palette.grey[600],
                fontSize: 11,
                textAnchor: "middle",
              })}
            />

            {data.length > 0 && (
              <>
                <AreaClosed
                  data={data}
                  x={(d) => xScale(d.timestamp)}
                  y={(d) => yScale(d.openRate)}
                  yScale={yScale}
                  fill={`url(#${GRADIENT_ID})`}
                  curve={curveMonotoneX}
                  pointerEvents="none"
                />
                <LinePath
                  data={data}
                  x={(d) => xScale(d.timestamp)}
                  y={(d) => yScale(d.openRate)}
                  stroke={LINE_COLOR}
                  strokeWidth={2.5}
                  curve={curveMonotoneX}
                  pointerEvents="none"
                />
                {data.map((d, i) => (
                  <circle
                    key={i}
                    cx={xScale(d.timestamp)}
                    cy={yScale(d.openRate)}
                    r={hoveredIndex === i ? 6 : 4}
                    fill={hoveredIndex === i ? LINE_COLOR : "#fff"}
                    stroke={LINE_COLOR}
                    strokeWidth={2}
                    pointerEvents="none"
                  />
                ))}
              </>
            )}

            {/* Vertical dashed line on hover */}
            {hoveredPoint && (
              <line
                x1={xScale(hoveredPoint.timestamp)}
                x2={xScale(hoveredPoint.timestamp)}
                y1={0}
                y2={innerHeight}
                stroke={theme.palette.grey[400]}
                strokeWidth={1}
                strokeDasharray="4 3"
                pointerEvents="none"
              />
            )}

            {/* invisible interaction layer */}
            <rect
              x={0}
              y={0}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </Group>

          {/* Tooltip */}
          {hoveredPoint &&
            (() => {
              const cx = xScale(hoveredPoint.timestamp) + MARGIN.left;
              const cy = yScale(hoveredPoint.openRate) + MARGIN.top;
              const tx = Math.max(
                4,
                Math.min(cx - tooltipW / 2, width - tooltipW - 4)
              );
              const ty = Math.max(4, cy - tooltipH - 14);
              return (
                <g pointerEvents="none">
                  <rect
                    x={tx}
                    y={ty}
                    width={tooltipW}
                    height={tooltipH}
                    rx={6}
                    fill="#1a2340"
                  />
                  <text
                    x={tx + tooltipW / 2}
                    y={ty + 18}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.7)"
                    fontSize={11}
                  >
                    {new Date(hoveredPoint.timestamp).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                      }
                    )}
                  </text>
                  <text
                    x={tx + tooltipW / 2}
                    y={ty + 38}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={13}
                    fontWeight="600"
                  >
                    {hoveredPoint.openRate.toFixed(1)}% Open Rate
                  </text>
                  <text
                    x={tx + tooltipW / 2}
                    y={ty + 56}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.6)"
                    fontSize={11}
                  >
                    {hoveredPoint.opens} opens
                  </text>
                </g>
              );
            })()}
        </svg>
      )}
    </div>
  );
}

export default EngagerChart;
